import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function DonatePreview() {
  const { t } = useTranslation();
  const headerRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const badgesRef = useRef<HTMLDivElement>(null);
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);
  const [subscribeMsg, setSubscribeMsg] = useState<string | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("reveal-visible");
            const children = Array.from(entry.target.children) as HTMLElement[];
            children.forEach((child) => child.classList.add("reveal-visible"));
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
    );

    if (headerRef.current) {
      headerRef.current.classList.add("reveal");
      observer.observe(headerRef.current);
    }

    if (gridRef.current) {
      const children = Array.from(gridRef.current.children) as HTMLElement[];
      children.forEach((child, i) => {
        child.classList.add("reveal-child");
        child.style.transitionDelay = `${i * 150}ms`;
      });
      observer.observe(gridRef.current);
    }

    if (badgesRef.current) {
      badgesRef.current.classList.add("reveal");
      observer.observe(badgesRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubscribing(true);
    setSubscribeMsg(null);

    try {
      const formData = new URLSearchParams();
      formData.append("email", email.trim());
      formData.append("source", "donate_section");

      const honeypot = (document.getElementById("dn_website_alt") as HTMLInputElement)?.value?.trim();
      if (honeypot) {
        setSubscribeMsg(t("donate.newsletter.success"));
        setEmail("");
        setSubscribing(false);
        return;
      }

      const res = await fetch("https://readdy.ai/api/form/d93hlkdmi650so75dr40", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formData.toString(),
      });

      if (res.ok) {
        setSubscribeMsg(t("donate.newsletter.success"));
        setEmail("");
      } else {
        setSubscribeMsg(t("donate.newsletter.error"));
      }
    } catch {
      setSubscribeMsg(t("donate.newsletter.error"));
    } finally {
      setSubscribing(false);
    }
  };

  const tiers = [
    {
      amount: "$50",
      labelKey: "donate.tier.supporter",
      impact: "Provides school supplies for 5 students for one semester",
      items: ["Notebooks & pens", "Backpack", "Basic textbooks"],
      color: "border-gray-200 bg-white",
      badge: "",
    },
    {
      amount: "$150",
      labelKey: "donate.tier.champion",
      impact: "Funds a month of clean water access for a family of 6",
      items: ["Water purification tablets", "Storage containers", "Hygiene kit"],
      color: "border-[#3B9EF5] bg-[#3B9EF5]/5",
      badgeKey: "donate.mostPopular",
    },
    {
      amount: "$500",
      labelKey: "donate.tier.patron",
      impact: "Sponsors a girl's full year of secondary school education",
      items: ["Tuition fees", "Uniform & supplies", "Exam registration"],
      color: "border-[#1B2838] bg-[#1B2838]",
      badgeKey: "donate.highImpact",
      dark: true,
    },
  ];

  const trustBadges = [
    { icon: "ri-shield-check-line", labelKey: "donate.trust.charity" },
    { icon: "ri-eye-line", labelKey: "donate.trust.transparent" },
    { icon: "ri-file-text-line", labelKey: "donate.trust.receipts" },
    { icon: "ri-lock-line", labelKey: "donate.trust.secure" },
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12" ref={headerRef}>
          <span className="text-xs font-semibold uppercase tracking-widest text-[#3B9EF5] mb-3 block">
            {t("donate.tag")}
          </span>
          <h2
            className="text-3xl md:text-4xl font-bold text-[#1B2838] mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {t("donate.title")}
          </h2>
          <p className="text-gray-500 text-base max-w-xl mx-auto">{t("donate.subtitle")}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10" ref={gridRef}>
          {tiers.map((tier) => (
            <div
              key={tier.amount}
              className={`rounded-xl border-2 p-7 relative ${tier.color} transition-all duration-300`}
            >
              {tier.badgeKey && (
                <div
                  className={`absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold whitespace-nowrap ${
                    tier.dark ? "bg-[#3B9EF5] text-[#1B2838]" : "bg-[#1B2838] text-white"
                  }`}
                >
                  {t(tier.badgeKey)}
                </div>
              )}
              <div className={`text-4xl font-bold mb-1 ${tier.dark ? "text-white" : "text-[#1B2838]"}`}>
                {tier.amount}
              </div>
              <div className="text-sm font-semibold mb-4 text-[#3B9EF5]">{t(tier.labelKey)}</div>
              <p className={`text-sm leading-relaxed mb-5 ${tier.dark ? "text-gray-300" : "text-gray-600"}`}>
                {tier.impact}
              </p>
              <ul className="space-y-2 mb-6">
                {tier.items.map((item) => (
                  <li
                    key={item}
                    className={`flex items-center gap-2 text-sm ${tier.dark ? "text-gray-300" : "text-gray-500"}`}
                  >
                    <i className="ri-check-line text-[#3B9EF5]"></i>
                    {item}
                  </li>
                ))}
              </ul>
              <Link
                to="/donate"
                className={`block text-center py-3 rounded-full font-bold text-sm whitespace-nowrap cursor-pointer transition-all ${
                  tier.dark
                    ? "bg-[#3B9EF5] text-[#1B2838] hover:bg-[#2A8DE0]"
                    : "bg-[#1B2838] text-white hover:bg-[#2a3a4a]"
                }`}
              >
                {t("donate.donate")} {tier.amount}
              </Link>
            </div>
          ))}
        </div>

        {/* Newsletter signup */}
        <div className="max-w-lg mx-auto mb-10 bg-[#FAF8F5] rounded-xl p-6 border border-gray-100">
          <h3 className="text-lg font-bold text-[#1B2838] mb-2 text-center">{t("donate.newsletter.title")}</h3>
          <p className="text-gray-500 text-sm text-center mb-4">{t("donate.newsletter.desc")}</p>

          {subscribeMsg ? (
            <div className="text-center px-4 py-3 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-lg text-sm">
              <i className="ri-check-line text-emerald-500 mr-1.5"></i>
              {subscribeMsg}
            </div>
          ) : (
            <form onSubmit={handleNewsletterSubmit} data-readdy-form className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                id="dn_website_alt"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="absolute opacity-0 pointer-events-none"
              />
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("donate.newsletter.placeholder")}
                required
                className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#3B9EF5] focus:ring-1 focus:ring-[#3B9EF5] transition-colors"
              />
              <button
                type="submit"
                disabled={subscribing}
                className="px-5 py-2.5 bg-[#1B2838] text-white rounded-lg font-semibold text-sm cursor-pointer hover:bg-[#2a3a4a] transition-colors disabled:opacity-50 whitespace-nowrap"
              >
                {subscribing ? (
                  <span className="flex items-center gap-1.5">
                    <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    {t("donate.newsletter.subscribing")}
                  </span>
                ) : (
                  t("donate.newsletter.button")
                )}
              </button>
            </form>
          )}
        </div>

        {/* Trust badges */}
        <div
          className="flex flex-wrap justify-center gap-6 pt-8 border-t border-gray-100"
          ref={badgesRef}
        >
          {trustBadges.map((badge) => (
            <div key={badge.labelKey} className="flex items-center gap-2 text-gray-500 text-sm">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#3B9EF5]/10 text-[#3B9EF5]">
                <i className={`${badge.icon} text-base`}></i>
              </div>
              <span>{t(badge.labelKey)}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
